import * as React from 'react'

import { WithBadge } from '~ui/components/badges/WithBadge'
import { Text } from '~ui/components/typography/Text/Text'
import { TTextFontSize } from '~ui/styleGuide/fontStyles'
import { getSpacingValue, TSingleSpace } from '~ui/styleGuide/spaces'

type TSize = 'xs' | 'sm' | 'md' | 'lg'

type TProps = RequiredChildren & {
  productCount: number
  size: TSize
  productRightPadding?: TSingleSpace
}

const badgeOffset: PropsOf<typeof WithBadge>['offsetTop'] = `-0.7rem`

/**
 * renders the round badge with the current product count present in the cart
 */
export const WithProductCountBadge: React.FC<TProps> = ({
  children,
  size,
  productRightPadding = `none`,
  productCount,
}) => {
  const { badgeSize, fontSize } = getSizes(size)

  // prettier-ignore
  const badgeContent: PropsOf<typeof WithBadge>['badgeContent'] = productCount > 0
    ? <Text contrast={`high`} fontSize={fontSize}>{productCount}&times;</Text>
    : null

  return (
    <WithBadge
      bgColor={`specialColors.company`}
      size={badgeSize}
      offsetTop={badgeOffset}
      offsetRight={`calc(${badgeOffset} + ${getSpacingValue(productRightPadding)})`}
      badgeContent={badgeContent}
    >
      {children}
    </WithBadge>
  )
}

type TGetSize = (
  size: TSize,
) => { badgeSize: PropsOf<typeof WithBadge>['size']; fontSize: TTextFontSize }

// translate the size label to a value and bind the text content font size to it
const getSizes: TGetSize = (size) => {
  switch (size) {
    case 'xs':
      return { badgeSize: `1.5rem`, fontSize: `s12` }
    case 'sm':
      return { badgeSize: `2rem`, fontSize: `s12` }
    case 'md':
      return { badgeSize: `2.5rem`, fontSize: `s14` }
    case 'lg':
      return { badgeSize: `3rem`, fontSize: `m16` }
  }
}
